// src/components/email/ReplyForm.tsx

import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Modal,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { ImapEmail } from "../../../types/imapEmail";

/** If your staff can pick which mailbox they're replying from */
const FROM_EMAIL_OPTIONS = [
  "admin@hbinternational.org",
  "support@hbinternational.org",
  "info@hbinternational.org",
];

interface ReplyFormProps {
  originalEmail: ImapEmail;
  onClose: () => void;
  /**
   * We'll call onSend() with a payload that matches /emails/send:
   * {
   *   fromEmail,
   *   recipient,
   *   subject,
   *   text, // final typed + quoted text
   *   ...
   * }
   */
  onSend: (replyData: any) => void;
}

const ReplyForm: React.FC<ReplyFormProps> = ({ originalEmail, onClose, onSend }) => {
  // Pre-populate subject with "Re:" if missing
  const subject = originalEmail.subject?.startsWith("Re:")
    ? originalEmail.subject
    : `Re: ${originalEmail.subject || ""}`;

  const [replyText, setReplyText] = useState("");
  const [includeOriginal, setIncludeOriginal] = useState(true);

  // If your staff can choose the "from" mailbox:
  const [fromEmail, setFromEmail] = useState(FROM_EMAIL_OPTIONS[0]);

  // Additional robust fields
  const [recipientName, setRecipientName] = useState("");
  const [signature, setSignature] = useState("");

  /**
   * Generate quoted text from originalEmail.text if user wants it included.
   */
  const getQuotedText = (): string => {
    if (!originalEmail.text) return "";
    return originalEmail.text
      .split("\n")
      .map(line => `> ${line}`)
      .join("\n");
  };

  /**
   * By default, replying sets "recipient" to originalEmail.from.
   */
  const getRecipient = (): string => {
    if (Array.isArray(originalEmail.to) && originalEmail.to.length > 0) {
      return originalEmail.to[0];
    } else if (typeof originalEmail.to === "string") {
      return originalEmail.to;
    }
    // fallback
    return "unknown@invalid.com";
  };

  const handleSend = () => {
    const fullReply = includeOriginal
      ? `${replyText}\n\n${getQuotedText()}`
      : replyText;

    // Build final payload to pass to handleSendReply in EmailLayout
    const payload = {
      fromEmail,
      recipient: getRecipient(),
      subject,
      text: fullReply || " ", // rename "text" so that handleSendReply can do message: text
      recipientName: recipientName || undefined,
      signature: signature || undefined,
      originalEmailId: originalEmail.id,
    };

   ////console.log("ReplyForm: sending payload:", payload);
    onSend(payload);
  };

  return (
    <Modal open onClose={onClose}>
      <Paper
        sx={{
          margin: "auto",
          padding: 2,
          maxWidth: 600,
          mt: 10,
          position: "relative",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Reply to{" "}
          {typeof originalEmail.from === "object"
            ? originalEmail.from.email
            : originalEmail.from}
        </Typography>

        {/* If user can pick "From" address */}
        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
          <InputLabel>From Email</InputLabel>
          <Select
            label="From Email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value as string)}
          >
            {FROM_EMAIL_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Recipient Name (optional)"
          fullWidth
          margin="normal"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
        />

        <TextField
          label="Subject"
          fullWidth
          margin="normal"
          value={subject}
          disabled
        />

        <TextField
          label="Message"
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={includeOriginal}
              onChange={(e) => setIncludeOriginal(e.target.checked)}
            />
          }
          label="Include original message"
          sx={{ mt: 1 }}
        />

        <TextField
          label="Signature (optional)"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button onClick={onClose} color="secondary" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button onClick={handleSend} variant="contained" color="primary">
            Send
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ReplyForm;
